const emptyLeaf = () => ({ field: "", operator: "", values: "" });
const emptySubgroup = () => ({ operator: "And", conditions: [emptyLeaf()] });
const emptyTree = () => ({ operator: "And", conditions: [] });

const leafFromBackend = (c) => ({
  field: c.left || "",
  operator: c.operator || "",
  values: c.right ?? "",
});

const backendToLocal = (criteria) => {
  if (!criteria) return emptyTree();

  if ("left" in criteria) {
    return { operator: "And", conditions: [leafFromBackend(criteria)] };
  }

  if (criteria.operator && Array.isArray(criteria.conditions)) {
    return {
      operator: criteria.operator,
      conditions: criteria.conditions
        .map((cond) => {
          if ("left" in cond) return leafFromBackend(cond);
          if (cond.operator && Array.isArray(cond.conditions)) {
            return {
              operator: cond.operator,
              conditions: cond.conditions
                .map((leaf) => ("left" in leaf ? leafFromBackend(leaf) : null))
                .filter(Boolean),
            };
          }
          return null;
        })
        .filter(Boolean),
    };
  }

  return emptyTree();
};

const serializeLeaf = (item) => {
  if (!item.field || !item.operator) return null;

  let right = item.values;
  let operator = item.operator;

  if (operator === "In" || operator === "NotIn") {
    if (typeof right === "string") {
      const arr = right
        .split(",")
        .map((v) => v.trim())
        .filter((v) => v !== "");
      if (arr.length === 1) {
        operator = operator === "In" ? "Eq" : "Neq";
        right = arr[0];
      } else if (arr.length > 1) {
        right = arr;
      }
    }
    if (Array.isArray(right) && right.length === 1) {
      operator = operator === "In" ? "Eq" : "Neq";
      right = right[0];
    }
  }

  return { left: item.field, operator, right };
};

const localToBackend = (tree) => {
  if (!tree || !tree.conditions) return null;

  const conditions = tree.conditions
    .map((item) => {
      if (item.conditions !== undefined) {
        const subConditions = item.conditions
          .map(serializeLeaf)
          .filter(Boolean);
        if (subConditions.length === 0) return null;
        // Preserve subgroup wrapper even for a single leaf so subgroup AND/OR
        // logic remains scoped to the subgroup instead of being flattened.
        return { operator: item.operator, conditions: subConditions };
      }
      return serializeLeaf(item);
    })
    .filter(Boolean);

  if (conditions.length === 0) return null;
  if (conditions.length === 1) return conditions[0];
  return { operator: tree.operator, conditions };
};

const buildExpression = (tree, criteriaOptions) => {
  if (!tree || !tree.conditions || tree.conditions.length === 0) return "";

  const itemLabel = (leaf) => {
    const match = criteriaOptions.find((c) => c.id === leaf.field);
    return match ? match.description || leaf.field : leaf.field || "?";
  };

  const formatLeaf = (leaf) => {
    if (!leaf.field) return null;
    const val = Array.isArray(leaf.values)
      ? leaf.values.join(", ")
      : leaf.values || "";
    return `${itemLabel(leaf)} ${leaf.operator} ${val}`.trim();
  };

  const formatGroup = (group, isRoot = false) => {
    if (!group || !Array.isArray(group.conditions))
      return { expr: "", count: 0 };

    const parts = group.conditions
      .map((item) => {
        if (item.conditions !== undefined) {
          const formatted = formatGroup(item, false);
          return {
            expr: formatted.expr,
            count: formatted.count,
            isSubgroup: true,
            subgroupOperator: item.operator || "And",
          };
        }
        return {
          expr: formatLeaf(item),
          count: 1,
          isSubgroup: false,
          subgroupOperator: null,
        };
      })
      .filter((part) => part.expr);

    if (parts.length === 0) return { expr: "", count: 0 };

    const defaultConnector = (group.operator || "And").toUpperCase();

    if (isRoot) {
      let expr = parts[0].expr;
      let currentCount = 1;

      for (let i = 1; i < parts.length; i++) {
        const part = parts[i];
        const connector = (
          part.isSubgroup && part.count === 1
            ? part.subgroupOperator || group.operator || "And"
            : group.operator || "And"
        ).toUpperCase();

        const leftExpr =
          connector !== defaultConnector && currentCount > 1
            ? `(${expr})`
            : expr;
        expr = `${leftExpr} ${connector} ${part.expr}`;
        currentCount++;
      }

      return { expr, count: parts.length };
    }

    const joiner = ` ${defaultConnector} `;
    const expr = parts.map((part) => part.expr).join(joiner);
    return {
      expr: parts.length === 1 ? expr : `(${expr})`,
      count: parts.length,
    };
  };

  return formatGroup(tree, true).expr;
};

export {
  emptyLeaf,
  emptySubgroup,
  emptyTree,
  backendToLocal,
  localToBackend,
  buildExpression,
};
